import { router, usePage } from "@inertiajs/react"
import { useLayoutEffect } from "react"
import { useAuth } from "@/hooks/use-permissions"
import {
  appendTaskColumnPage,
  changeTaskCommentsCount,
  filterTasksByPerson,
  parseTaskPersonFilter,
  placeTaskCard,
  recountUnloadedTask,
  removeTaskRow,
  taskCardRow,
  taskCardUnderSearch,
  type TaskBoardRows,
} from "@/lib/task-board"
import type {
  TaskBoardSearch,
  TaskCardRow,
  TaskColumnCursor,
  TaskColumnPage,
  TaskColumnsState,
  TaskDraftRow,
  TaskPerson,
  TaskRow,
  TaskStatus,
} from "@/types/tasks"

type BoardProps = {
  tasks?: TaskCardRow[]
  task_columns?: TaskColumnsState
  selected_task?: TaskRow | null
  selected_task_id?: number | null
  project?: { id: number }
  projects?: { id: number }[]
  project_id?: number | null
  people?: TaskPerson[]
  members?: TaskPerson[]
  members_by_project?: Record<string, TaskPerson[]>
  task_draft?: TaskDraftRow | null
  task_search?: TaskBoardSearch | null
}

type BoardRows = TaskBoardRows & { selected_task: TaskRow | null }

// Пропсы отрисованной доски: по ним видно заранее, что правка ничего не меняет, и визит не нужен.
let renderedBoard: BoardProps | null = null
let renderedUrl = ""
let renderedUserId: number | null = null
// Ответ на сохранение, пришедший после удаления задачи, не должен вернуть её карточку.
const droppedTaskIds = new Set<number>()
// То же для черновика формы новой задачи: после «Удалить» или «Создать» его строка в пропсы не возвращается.
const droppedDraftIds = new Set<number>()
// Самая свежая строка задачи, оставшейся без карточки из-за поиска: более старый ответ карточку не вернёт.
const cardlessTaskAt = new Map<number, string>()

export function useTaskBoardSnapshot() {
  const { props, url } = usePage<BoardProps>()
  const userId = useAuth()?.user?.id ?? null
  useLayoutEffect(() => {
    renderedBoard = props
    renderedUrl = url
    renderedUserId = userId
    return () => {
      if (renderedBoard === props) renderedBoard = null
    }
  }, [props, url, userId])
}

export function boardSearchQuery(): string {
  return renderedBoard?.task_search?.q ?? ""
}

// Тот же фильтр доски (люди и поиск), под которым сервер считал «осталось»: неизвестный доске человек фильтра не даёт.
function boardFilterMatches(props: BoardProps): (row: TaskCardRow) => boolean {
  const filter = parseTaskPersonFilter(renderedUrl, renderedUserId)
  const members = props.members ?? Object.values(props.members_by_project ?? {}).flat()
  const known = [...(props.people ?? []), ...members].some((person) => person.user_id === filter.person)
  const byPerson = filter.person != null && (filter.person === "me" || known)
  const searching = props.task_search != null
  return (row) =>
    (!byPerson || filterTasksByPerson([row], filter, renderedUserId).length > 0) &&
    (!searching || row.search?.match === true)
}

function boardSearchKnown(props: BoardProps, ...rows: TaskCardRow[]): boolean {
  const search = props.task_search ?? null
  return search == null || rows.every((row) => row.search?.q === search.q)
}

function nextBoardProps(props: BoardProps, update: (rows: BoardRows, props: BoardProps) => BoardRows): BoardProps {
  if (!Array.isArray(props.tasks) || !("selected_task_id" in props)) return props
  const rows = { tasks: props.tasks, columns: props.task_columns ?? null, selected_task: props.selected_task ?? null }
  const next = update(rows, props)
  if (next.tasks === rows.tasks && next.columns === rows.columns && next.selected_task === rows.selected_task) {
    return props
  }
  return {
    ...props,
    tasks: next.tasks,
    selected_task: next.selected_task,
    ...(next.columns ? { task_columns: next.columns } : {}),
  }
}

// Правка на клиенте без запроса: сервер уже вернул свежую строку, перезапрашивать доску незачем.
// Ответ может прийти, когда открыта уже другая страница, — тогда трогать нечего.
// Снимок верен, только пока в очереди Inertia нет наших правок: иначе «ничего не меняет» считалось бы по старому.
let queuedBoardUpdates = 0

function replaceBoardProps(update: (props: BoardProps) => BoardProps): Promise<void> {
  const board = renderedBoard
  if (board == null) return Promise.resolve()
  if (queuedBoardUpdates === 0 && update(board) === board) return Promise.resolve()
  queuedBoardUpdates += 1
  return new Promise((resolve) =>
    router.replace<BoardProps>({
      preserveScroll: true,
      preserveState: true,
      props: update,
      onFinish: () => {
        queuedBoardUpdates -= 1
        resolve()
      },
    }),
  )
}

function updateBoard(update: (rows: BoardRows, props: BoardProps) => BoardRows): Promise<void> {
  return replaceBoardProps((props) => nextBoardProps(props, update))
}

// Черновик формы новой задачи из ответа сервера; более старая строка того же черновика пропсы не трогает.
export function applyTaskDraft(draft: TaskDraftRow): Promise<void> {
  if (droppedDraftIds.has(draft.id)) return Promise.resolve()
  return replaceBoardProps((props) => {
    if (!("task_draft" in props)) return props
    const current = props.task_draft ?? null
    if (current?.id === draft.id && current.updated_at >= draft.updated_at) return props
    return { ...props, task_draft: draft }
  })
}

export function dropTaskDraft(draftId: number): Promise<void> {
  droppedDraftIds.add(draftId)
  return replaceBoardProps((props) =>
    "task_draft" in props && props.task_draft?.id === draftId ? { ...props, task_draft: null } : props,
  )
}

function boardShowsProject(props: BoardProps, projectId: number): boolean {
  if (props.project) return props.project.id === projectId
  const visible = (props.projects ?? []).some((project) => project.id === projectId)
  return visible && (props.project_id == null || props.project_id === projectId)
}

function upsertCard(rows: BoardRows, row: TaskRow, props: BoardProps, extendCursor = false): BoardRows {
  if (droppedTaskIds.has(row.id)) return rows
  const current = rows.tasks.find((task) => task.id === row.id) ?? null
  if (!current && !boardShowsProject(props, row.project_id)) return rows
  const card = taskCardUnderSearch(row, current, props.task_search ?? null)
  const cardlessAt = cardlessTaskAt.get(row.id) ?? ""
  if (card == null) {
    if (current && current.updated_at > row.updated_at) return rows
    if (row.updated_at > cardlessAt) cardlessTaskAt.set(row.id, row.updated_at)
    return current ? { ...rows, tasks: removeTaskRow(rows.tasks, row.id) } : rows
  }
  if (!current && cardlessAt > row.updated_at) return rows
  const placed = placeTaskCard(rows, card, extendCursor)
  return placed === rows ? rows : { ...rows, tasks: placed.tasks, columns: placed.columns }
}

export function applyTaskRow(row: TaskRow, { extendCursor = false }: { extendCursor?: boolean } = {}): Promise<void> {
  return updateBoard((rows, props) => {
    const selected = rows.selected_task
    const fresh = selected?.id === row.id && row.updated_at >= selected.updated_at
    const placed = upsertCard(rows, row, props, extendCursor)
    const counted =
      fresh && selected && !droppedTaskIds.has(row.id) && boardSearchKnown(props, selected, row)
        ? recountUnloadedTask(rows, placed, taskCardRow(selected), taskCardRow(row), boardFilterMatches(props))
        : placed
    return { ...counted, selected_task: fresh ? row : selected }
  })
}

export function showTaskDetail(row: TaskRow | null): Promise<void> {
  return updateBoard((rows, props) => {
    if (row == null) return { ...rows, selected_task: null }
    const selected = rows.selected_task
    const newer = selected?.id === row.id && selected.updated_at > row.updated_at
    return { ...upsertCard(rows, row, props), selected_task: newer ? selected : row }
  })
}

export function dropTaskRow(taskId: number): Promise<void> {
  droppedTaskIds.add(taskId)
  return updateBoard((rows, props) => {
    const selected = rows.selected_task?.id === taskId ? rows.selected_task : null
    const removed = { ...rows, tasks: removeTaskRow(rows.tasks, taskId) }
    const counted =
      selected && boardSearchKnown(props, selected)
        ? recountUnloadedTask(rows, removed, taskCardRow(selected), null, boardFilterMatches(props))
        : removed
    return { ...counted, selected_task: selected ? null : rows.selected_task }
  })
}

export function shiftTaskCommentsCount(taskId: number, delta: number): Promise<void> {
  return updateBoard((rows) => ({ ...rows, tasks: changeTaskCommentsCount(rows.tasks, taskId, delta) }))
}

export function appendColumnPage(status: TaskStatus, page: TaskColumnPage, after: TaskColumnCursor): Promise<void> {
  const tasks = page.tasks.filter((row) => !droppedTaskIds.has(row.id))
  return updateBoard((rows) => {
    const appended = appendTaskColumnPage(rows, status, { tasks, column: page.column }, after)
    return appended === rows ? rows : { ...rows, tasks: appended.tasks, columns: appended.columns }
  })
}
